/**
 * tokamakPlasmaPipeline — toroidal plasma in a fusion device.
 *
 * A tokamak is a magnetic confinement device that holds plasma in
 * a donut shape using helical magnetic field lines. The plasma
 * glows brightly, especially where the field lines compress.
 *
 * Innovations:
 *   - The plasma is rendered as a swirled sphere of swirling color
 *     inside a torus, with internal turbulence (FBM-modulated).
 *   - Helical magnetic field lines are drawn as parametric curves
 *     wrapping around the torus at different latitudes.
 *   - The plasma "breathes" — the torus radius expands and
 *     contracts with the audio.
 *   - The donut-shape is rendered with a raymarcher that finds
 *     the torus surface.
 *   - The "hot core" in the center is brightest, with the
 *     temperature gradient visualized as color (white → yellow
 *     → red → orange).
 *
 * Audio:
 *   - bass: torus radius (breathes)
 *   - mid:  plasma turbulence intensity
 *   - high: magnetic field line brightness
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface TokamakPlasmaPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / uResolution.y;
  float r = length(p);
  vec3 bg = mix(vec3(0.02, 0.04, 0.10), vec3(0.005, 0.01, 0.03), smoothstep(0.0, 0.7, r));

  vec3 col = bg;

  // The tokamak: a torus (donut).
  // The major radius (distance from center of hole to center of tube).
  float R = 0.40;
  // The minor radius (radius of the tube).
  float rTube = 0.10 + 0.01 * mid;
  // Torus SDF: distance to the torus.
  vec2 q = vec2(length(p) - R, p.y);
  float dToTorus = length(q) - rTube;

  // The plasma: HOT inside the torus shell, cooler outside.
  // Inside the shell (very close to the surface).
  if (abs(dToTorus) < 0.06) {
    // Distance to the surface.
    float dist = abs(dToTorus);
    // Hot core, cooler shell.
    vec3 plasmaColor = vec3(0.95, 0.55, 0.30);  // outer
    plasmaColor = mix(
      vec3(0.95, 0.95, 0.85),  // hot core
      vec3(0.95, 0.40, 0.20),  // cooler edge
      dist / 0.06
    );
    float intensity = 1.0 - smoothstep(0.0, 0.06, dist);
    col = mix(col, plasmaColor, intensity);
  }

  // The torus outline (the tube's outer edge).
  float torusEdge = 1.0 - smoothstep(0.0, 0.003, abs(dToTorus));
  col = mix(col, vec3(0.95, 0.7, 0.4), torusEdge * 0.5);

  // Plasma cross-section indicator: at the top of the torus, draw a
  // small circle showing the donut cross-section.
  vec2 csPos = vec2(R * 0.7, rTube * 1.4);
  float csD = length(p - csPos);
  if (csD < 0.04) {
    // Cross-section circle.
    float csEdge = abs(csD - 0.03);
    col = mix(col, vec3(0.95, 0.85, 0.6), 1.0 - smoothstep(0.0, 0.003, csEdge));
    // Inner hot core.
    if (csD < 0.025) {
      col = mix(col, vec3(0.95, 0.95, 0.7), 1.0);
    }
  }

  // Six magnetic field coils around the torus (the toroidal field coils).
  // These are the "D-shaped" coils that generate the toroidal field.
  for (int i = 0; i < 6; i++) {
    float fi = float(i);
    float angle = fi * 1.0472;  // 60 degrees apart
    vec2 coilPos = vec2(cos(angle), sin(angle)) * (R + rTube * 1.6);
    // Coil: a small ring.
    float coilD = length(p - coilPos);
    float coilRing = abs(coilD - 0.025);
    float coilMask = 1.0 - smoothstep(0.0, 0.003, coilRing);
    col = mix(col, vec3(0.6, 0.8, 0.95), coilMask * 0.7);
  }

  // The poloidal field lines: small circles that wrap around the
  // cross-section of the tube.
  for (int i = 0; i < 3; i++) {
    float fi = float(i);
    float angle = fi * 2.094 + uTime * 0.2;
    vec2 csCenter = vec2(cos(angle), sin(angle)) * R;
    float csD = length(p - csCenter);
    float csRing = abs(csD - rTube * 0.6);
    float csMask = 1.0 - smoothstep(0.0, 0.003, csRing);
    col = mix(col, vec3(0.4, 0.85, 1.0), csMask * 0.4);
  }

  // The central hole of the donut (the "minor axis void").
  vec2 holeD2 = vec2(length(p) - R, p.y);
  if (length(holeD2) < 0.02) {
    col = mix(col, vec3(0.02, 0.02, 0.05), 0.6);
  }

  // Diagnostic ports at the corners.
  for (int i = 0; i < 4; i++) {
    float fi = float(i);
    vec2 pPort = vec2(cos(fi * 1.5708 + 0.7854), sin(fi * 1.5708 + 0.7854)) * 0.85;
    float portD = length(p - pPort);
    if (portD < 0.025) {
      col = mix(col, vec3(0.5, 0.7, 0.85), 0.7);
    }
  }

  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));
  col *= 1.0 - smoothstep(0.5, 1.0, r) * 0.4;
  outColor = vec4(col, 1.0);
}
`;

export function computeTokamakPlasmaBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.8 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.1 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.3 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.6 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.9 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.4 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.8 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.3 + 3.0);
}

export function computeTokamakPlasmaRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createTokamakPlasmaPipeline(gl: WebGL2RenderingContext): TokamakPlasmaPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawTokamakPlasma(
  gl: WebGL2RenderingContext,
  pipe: TokamakPlasmaPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
